import React from 'react';
import { Card, CardHeader, CardTitle, CardDescription, CardContent, CardFooter } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { FolderKanban, MessageSquare, X, FileText } from 'lucide-react';
import { useSupabaseStore } from '@/store/SupabaseStore';
import { toast } from '@/components/ui/use-toast';
import { Project } from '@/types';
import { useNavigate } from 'react-router-dom';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "@/components/ui/alert-dialog"


type ProjectCardProps = {
  project: Project & { chatCount: number; docCount?: number };
  onOpenProject?: (project: Project & { chatCount: number; docCount?: number }) => void;
};

const ProjectCard = ({ project, onOpenProject }: ProjectCardProps) => {
  const { deleteProject } = useSupabaseStore();
  const navigate = useNavigate();

  const handleDelete = async () => {
    try {
      await deleteProject(project.id);
      toast({ title: "Project deleted" });
    } catch (error) {
      toast({ title: "Error deleting project", variant: "destructive" });
    }
  };

  const handleCardClick = () => {
    if (onOpenProject) {
      onOpenProject(project);
      return;
    }

    navigate(`/projects/${project.id}`);
  };

  return (
    <Card 
      className="hover:shadow-lg transition-shadow flex flex-col cursor-pointer"
      onClick={handleCardClick}
    >
      <CardHeader>
        <div className="flex items-start justify-between gap-3">
          <CardTitle className="flex min-w-0 flex-1 items-center gap-2">
            <FolderKanban className="h-5 w-5 shrink-0 text-primary" />
            <span className="truncate">{project.name}</span>
          </CardTitle>
          <AlertDialog>
            <AlertDialogTrigger asChild>
              <Button 
                variant="ghost" 
                size="icon" 
                className="shrink-0 text-muted-foreground hover:text-white"
                onClick={(e) => e.stopPropagation()}
              >
                <X className="h-4 w-4" />
              </Button>
            </AlertDialogTrigger>
            <AlertDialogContent onClick={(e) => e.stopPropagation()}>
              <AlertDialogHeader>
                <AlertDialogTitle>Are you sure?</AlertDialogTitle>
                <AlertDialogDescription>
                  This will permanently delete the "{project.name}" project and unassign all its chats. This action cannot be undone.
                </AlertDialogDescription>
              </AlertDialogHeader>
              <AlertDialogFooter>
                <AlertDialogCancel>Cancel</AlertDialogCancel>
                <AlertDialogAction onClick={handleDelete}>Delete</AlertDialogAction>
              </AlertDialogFooter>
            </AlertDialogContent>
          </AlertDialog>
        </div>
        <CardDescription className="pt-2 h-12 overflow-hidden text-ellipsis">{project.description || 'No description.'}</CardDescription>
      </CardHeader>
      <CardContent className="flex-grow">
      </CardContent>
      <CardFooter>
        <div className="flex items-center gap-5 text-sm text-muted-foreground">
          <div className="flex items-center gap-2">
            <MessageSquare className="h-4 w-4" />
            <span>{project.chatCount}</span>
          </div>
          <div className="flex items-center gap-2">
            <FileText className="h-4 w-4" />
            <span>{project.docCount || 0}</span>
          </div>
        </div>
      </CardFooter>
    </Card>
  );
};

export default ProjectCard;